/* ISW-27 — keep the flash-sale price on screen when the shopper switches variant.
 *
 * Why this file has to exist at all:
 *
 * theme.min.js's _updatePrice does `this.$productPrice.html(e)` — a full innerHTML
 * replace on [data-product-price] — on EVERY variant change, rebuilding <del>/<ins>
 * from the variant's raw price/compare_at_price. So the flash price that
 * snippets/product-price-single.liquid renders server-side is destroyed the moment the
 * shopper picks a different colour or size, and the original price reappears. The theme
 * has no "don't overwrite" flag and no partial-update path.
 *
 * The one hook available is the `variant:changed` event, which the theme fires on the
 * product container AFTER its own handlers have run:
 *
 *   this.$container.trigger({type:"variant:changed",currentVariant:t,oldVariant:...})
 *
 * Two things about that event dictate the implementation below:
 *
 *  1. It is a jQuery trigger of a custom (non-native) event type. jQuery does not
 *     dispatch a real DOM event for those, so addEventListener('variant:changed') never
 *     fires — this must bind through jQuery.
 *  2. The theme's jQuery is namespaced `window.jQuery_T4NT` (see assets/global.min.js).
 *     It is not `$` and not `window.jQuery`.
 *
 * Bound on `document` rather than per-container so quick view and quick shop, which are
 * injected by AJAX long after load, are covered without re-initialising anything.
 *
 * Loaded globally (layout/theme.liquid) because quick view can be opened from any page,
 * not just the product template. It costs one delegated listener and returns immediately
 * on pages with no flash-sale markup.
 */
(function () {
  'use strict';

  var $ = window.jQuery_T4NT;
  // No theme jQuery means no variant:changed event to listen for either, so the
  // server-rendered flash price is simply left alone.
  if (!$ || window.__iccFlashSaleBound) return;
  window.__iccFlashSaleBound = true;

  var SEL = '[data-flash-sale]';

  function money(cents) {
    var T = window.T4SThemeSP;
    if (T && T.Currency && typeof T.Currency.formatMoney === 'function') {
      try { return T.Currency.formatMoney(cents); } catch (e) { /* fall through */ }
    }
    return (cents / 100).toFixed(2);
  }

  /* Same element order and class names as the Liquid helper, so the CSS in
     snippets/head_assets.liquid (ISW-28) applies without a JS-specific rule. */
  function markup(cfg, origCents) {
    /* No inline percentage: it lives only in the corner badge, matching the Liquid
       helper and every ordinary sale card on the site. data-fs-* is repeated here so a
       repaint keeps the attributes the corner-badge patch reads. */
    return '<span class="t4s-fs-price" data-flash-sale-price' +
      ' data-fs-sale="' + cfg.sale + '" data-fs-orig="' + origCents + '">' +
      '<span class="t4s-fs-price__sale">' + money(cfg.sale) + '</span>' +
      '<s class="t4s-fs-price__orig">' + money(origCents) + '</s>' +
      '</span>';
  }

  function paint(el, origCents) {
    var cfg;
    try {
      cfg = JSON.parse(el.getAttribute('data-flash-sale'));
    } catch (e) {
      return;
    }
    if (!cfg || typeof cfg.sale !== 'number') return;
    /* _updatePrice replaces the children of [data-product-price]. When the flash branch
       rendered, that attribute is on this same element, so fall back to it. */
    var target = el.querySelector('[data-product-price]') || el;
    target.innerHTML = markup(cfg, origCents);
  }

  function isMainProduct(container) {
    if (!container || !container.getAttribute) return false;
    var raw = container.getAttribute('data-product-featured');
    if (!raw) return false;
    try {
      return JSON.parse(raw).isMainProduct === true;
    } catch (e) {
      return false;
    }
  }

  /* The card's corner badge is rendered client-side by the theme from the card's own
     price JSON (compare_at vs price), so it knows nothing about the flash price and
     showed a different percentage from the one beside the price -- -36% against -30% on
     the same card.

     Rather than replicate the theme's badge markup and locale strings, this rewrites the
     `price` the theme is about to read, to the flash price. The theme then does its own
     maths against compare_at and arrives at the figure we want, in its own markup.

     Timing: this file is deferred and loaded immediately after global.min.js, whereas
     theme.min.js is fetched later by render_bottom, so this runs first. Cards injected
     afterwards by AJAX (quick view, load more) are caught by the observer below. */
  /* Cards carry their config on [data-product-options]; the product page carries the
     equivalent on [data-product-featured]. Both feed the same badge maths, so both need
     patching -- the product page was still showing -50% against the price's -65% until
     the second selector was added. */
  var CFG_ATTRS = ['data-product-options', 'data-product-featured'];
  var CFG_SELECTOR = '[' + CFG_ATTRS.join('],[') + ']';

  function patchCardPrice(el) {
    var sale = parseInt(el.getAttribute('data-fs-sale'), 10);
    if (!(sale > 0)) return;

    /* One closest() over both attributes, so the *nearest* host wins. Testing them
       separately meant a card sitting inside a featured-product section matched that
       section's [data-product-featured] as well as its own [data-product-options], and
       patched the section with the card's price. */
    var host = el.closest(CFG_SELECTOR);
    if (!host || host.hasAttribute('data-fs-patched')) return;

    var attr = null;
    for (var i = 0; i < CFG_ATTRS.length; i++) {
      if (host.hasAttribute(CFG_ATTRS[i])) { attr = CFG_ATTRS[i]; break; }
    }
    if (!attr) return;

    var cfg;
    try {
      cfg = JSON.parse(host.getAttribute(attr));
    } catch (err) {
      return;
    }
    if (!cfg) return;

    cfg.price = sale;

    /* The theme only draws a sale badge when compare_at > price. A flash product with no
       compare_at of its own would therefore get no badge at all -- and since the inline
       percentage was removed, no percentage anywhere on the card. data-fs-orig already
       holds the baseline the Liquid struck through, so use it to give the theme something
       to measure against. */
    var orig = parseInt(el.getAttribute('data-fs-orig'), 10);
    if (orig > sale && !(cfg.compare_at_price > sale)) {
      cfg.compare_at_price = orig;
    }

    host.setAttribute(attr, JSON.stringify(cfg));
    host.setAttribute('data-fs-patched', '1');
  }

  function patchAllCards(root) {
    var scope = root && root.querySelectorAll ? root : document;
    Array.prototype.forEach.call(
      scope.querySelectorAll('[data-flash-sale-price][data-fs-sale]'),
      patchCardPrice
    );
  }

  /* When a campaign ends, every price on the page is wrong until something re-renders --
     they are all server-rendered Liquid. QA saw the countdown hit zero with the sale
     prices still on screen.

     Scheduled from data-fs-ends rather than from the countdown widget, because that widget
     only exists in the homepage section: hooking it would leave collection pages and
     product pages stale, which is the half of QA's report that matters most.

     Takes the earliest end on the page, since that is the first moment anything here can
     change. */
  function scheduleExpiryReload(root) {
    var nodes = (root || document).querySelectorAll('[data-fs-ends]');
    var soonest = 0;
    Array.prototype.forEach.call(nodes, function (el) {
      var ts = parseInt(el.getAttribute('data-fs-ends'), 10);
      if (ts > 0 && (soonest === 0 || ts < soonest)) soonest = ts;
    });
    if (!soonest) return;

    /* A couple of seconds of slack: reloading on the exact boundary can beat the server to
       it and simply re-render the sale, burning a reload for nothing. */
    var ms = (soonest * 1000) - Date.now() + 3000;

    /* Already over -- the page was served before the end and read after it. Reload now. */
    if (ms < 0) ms = 0;

    /* Nobody leaves a tab open for a day; setTimeout beyond that is not worth arming and
       overflows the 32-bit delay anyway. */
    if (ms > 86400000) return;

    if (window.__iccFlashSaleTimer) clearTimeout(window.__iccFlashSaleTimer);
    window.__iccFlashSaleTimer = setTimeout(function () {
      if (window.__iccFlashSaleReloaded) return;
      window.__iccFlashSaleReloaded = true;
      /* Cache-buster: Shopify serves cached HTML, and a plain reload can come back with
         the sale still on it -- which would look exactly like the bug being unfixed. */
      var url = new URL(window.location.href);
      url.searchParams.set('fs', String(Date.now()));
      window.location.replace(url.toString());
    }, ms);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', function () {
      patchAllCards(document);
      scheduleExpiryReload(document);
    });
  } else {
    patchAllCards(document);
    scheduleExpiryReload(document);
  }

  if (window.MutationObserver) {
    new MutationObserver(function (records) {
      for (var i = 0; i < records.length; i++) {
        for (var j = 0; j < records[i].addedNodes.length; j++) {
          var n = records[i].addedNodes[j];
          if (n.nodeType === 1) patchAllCards(n);
        }
      }
    }).observe(document.documentElement, { childList: true, subtree: true });
  }

  $(document).on('variant:changed', function (e) {
    var variant = e.currentVariant;
    /* No variant means the selection is unavailable; the theme hides the price entirely
       in that case, which is the right outcome, so leave it hidden. */
    if (!variant || typeof variant.price !== 'number') return;

    var container = e.target;
    if (!container || !container.querySelectorAll) return;

    /* Match the Liquid helper: the struck price is compare_at when the variant carries
       one higher than its selling price, otherwise the selling price. Reading it off the
       variant keeps the baseline correct per variant rather than per product. */
    var orig = variant.price;
    if (typeof variant.compare_at_price === 'number' && variant.compare_at_price > orig) {
      orig = variant.compare_at_price;
    }

    Array.prototype.forEach.call(container.querySelectorAll(SEL), function (el) {
      paint(el, orig);
    });

    /* The sticky add-to-cart bar sits outside [data-product-featured], which is why the
       theme keeps a separate $stickyPrice reference and overwrites that too. There is
       only ever one on a page and it always belongs to the main product, so only follow
       it when the event came from the main product — not from a quick view opened on top
       of it. */
    if (isMainProduct(container)) {
      var sticky = document.querySelector('[data-sticky-price]' + SEL);
      if (sticky) paint(sticky, orig);
    }
  });
})();
